import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Archive, CheckCircle2, XCircle, ChevronDown, ChevronRight, HelpCircle } from 'lucide-react';
import { formatDeliveryStatus, isBlockedDeliveryCode } from '@/lib/deliveryStatus';
import {
  NOT_HOMES_PASSED_REASONS, serviceFileEligibility, serviceFilePresence, houseNumberExtensionMismatch,
} from '@uniticket/shared';
import type { DmpAddressResponse, DmpAddressAttributes, BuildFileHit, ServiceFileCheckId } from '@uniticket/shared';

// Read-only DMP "Address reference" view: the address rows + supplementary attribute rows (status with
// the HOMES_CONNECT "!" badge, decoded delivery status, scopelists, twin JVID) + the connected-customer
// banner. Extracted from Panel 1 so the MDU per-apartment detail modal renders the SAME thing for a
// sibling. Pure presentational — the caller supplies the reference + attributes.

const REFERENCE_FIELDS = [
  { key: 'streetName', labelKey: 'fields.street' },
  { key: 'houseNumber', labelKey: 'fields.houseNumber' },
  { key: 'houseNumberExtension', labelKey: 'fields.houseNumberExt' },
  { key: 'busNumber', labelKey: 'fields.busNumber' },
  { key: 'postalCode', labelKey: 'fields.postalCode' },
  { key: 'municipality', labelKey: 'fields.municipality' },
  { key: 'city', labelKey: 'fields.city' },
  { key: 'dwellingUnitType', labelKey: 'fields.dwellingUnitType' },
] as const;

export function AddressReferenceView({
  reference,
  attributes,
  buildFiles,
  renderFix,
}: {
  reference: DmpAddressResponse | null;
  attributes: DmpAddressAttributes | null;
  /** Exports Build chargés — sert à confronter le verdict au fichier réellement envoyé. Facultatif :
   *  sans lui, le bloc rend quand même les dix conditions, il tait seulement la ligne du fichier. */
  buildFiles?: BuildFileHit[];
  /** Le bouton qui RÉPARE une condition bloquante, fourni par l'écran appelant.
   *
   *  ⚠️ SEULE LA CONSOLE DMP LE PASSE (contrainte user 2026-08-31 : « uniquement dans la console DMP,
   *  pas dans le reste »). La contrainte est ainsi tenue par la STRUCTURE et non par une convention :
   *  la fenêtre de validation et la fiche d'appartement ne fournissent rien, donc elles n'ont aucun
   *  bouton, et il est impossible d'en faire apparaître un chez elles par inadvertance. */
  renderFix?: (checkId: ServiceFileCheckId) => ReactNode;
}) {
  const { t } = useTranslation(['tickets']);
  // N19 (demande user 2026-09-14) : le numéro et son extension doivent commencer par le même chiffre.
  // Le défaut se teinte, il ne bloque RIEN — c'est de la donnée DMP existante, que nous n'avons pas
  // écrite ; la garde de SAISIE reste ailleurs, sur houseNumberExtensionCoherent.
  //
  // Le contrôle vit ici et nulle part ailleurs, et c'est ce qui couvre d'un coup les trois écrans
  // nommés par le user : ce composant est monté dans la console DMP, dans le Panneau 1 de la fenêtre
  // de validation, et dans la fiche par logement.
  const extMismatch = houseNumberExtensionMismatch(reference?.houseNumber, reference?.houseNumberExtension);
  // B-6 (demande user 2026-09-17, cause trouvée par la sonde de prod du 22-09) : une adresse ARCHIVÉE
  // (`deleted_at` non nul) se lit, mais ne s'écrit plus. Le composant le DÉDUIT de la référence qu'on
  // lui tend — donc les trois écrans où il est monté (console DMP, panneau 1, fiche par logement) le
  // disent de la même façon sans qu'aucun ait à y penser, exactement comme pour N19 au-dessus.
  //
  // Un instantané figé avant le 22-09 n'a pas le champ : `undefined` vaut « non archivée », jamais
  // l'inverse — dans le doute, la fiche reste celle d'avant. Le refus d'écriture, lui, ne dépend pas
  // de cet affichage : il tient au filtre `deleted_at IS NULL` que les chemins d'écriture gardent.
  const archivedAt = reference?.deletedAt ?? null;
  const archivedRows = archivedAt ? ' opacity-60' : '';
  return (
    <>
      {archivedAt && (
        <div className="mb-3 flex items-start gap-2 rounded-md border border-slate-300 bg-slate-100 px-3 py-2">
          <Archive className="mt-0.5 h-4 w-4 shrink-0 text-slate-600" aria-hidden="true" />
          <div className="min-w-0">
            <p className="text-xs font-semibold text-slate-700">
              {t('tickets:validation.archived.title', { date: new Date(archivedAt).toLocaleDateString() })}
            </p>
            <p className="text-[11px] text-slate-500">{t('tickets:validation.archived.hint')}</p>
          </div>
        </div>
      )}
      {attributes && <ServiceFileVerdict attributes={attributes} buildFiles={buildFiles} renderFix={renderFix} />}
      {attributes?.status === 'HOMES_CONNECT' && (
        <div className="mb-3 flex items-center gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2">
          <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />
          <span className="text-xs font-semibold text-red-700">{t('tickets:validation.statusConnectedWarning')}</span>
          {/* 22-10 — QUELLE connexion : son UTAC, à côté de l'avertissement. */}
          {attributes.utac && (
            <span className="font-mono text-xs text-red-700" title={t('tickets:validation.fields.utacHint')}>
              · {t('tickets:validation.fields.utac')} {attributes.utac}
            </span>
          )}
        </div>
      )}
      {reference ? (
        <div className={`divide-y divide-slate-100${archivedRows}`}>
          <RefRow label="JVID" value={reference.jvid} />
          {REFERENCE_FIELDS.map((f) => {
            const raw = (reference[f.key] as string | null) ?? '—';
            // Les DEUX lignes sont teintées, pas seulement l'extension : l'incohérence est une
            // RELATION entre elles, et ne rougir qu'un côté désignerait un coupable qu'on ne connaît
            // pas — c'est peut-être le numéro qui est faux.
            const flagged = extMismatch && (f.key === 'houseNumber' || f.key === 'houseNumberExtension');
            return (
              <RefRow
                key={f.key}
                label={t(`tickets:validation.${f.labelKey}`)}
                value={
                  flagged ? (
                    <span
                      className="inline-flex items-center gap-1.5 font-semibold text-red-700"
                      title={t('tickets:validation.houseExtMismatchHint')}
                    >
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                      {/* Une extension vide rendrait « — » en rouge sans rien dire : le mot manquant
                          est justement l'information, donc on l'écrit. */}
                      {!raw || raw === '—' ? t('tickets:validation.houseExtMissing') : raw}
                    </span>
                  ) : raw
                }
              />
            );
          })}
          <RefRow label={t('tickets:validation.fields.belmapAddressId')} value={reference.belmapAddressId ?? '—'} />
          <RefRow label={t('tickets:validation.fields.belmapBuildingId')} value={reference.belmapBuildingId ?? '—'} />
          <RefRow label={t('tickets:validation.fields.latLon')} value={reference.lat != null && reference.lon != null ? `${reference.lat} / ${reference.lon}` : '—'} />
        </div>
      ) : (
        <p className="text-sm text-slate-400 py-6 text-center">{t('tickets:validation.noReference')}</p>
      )}
      {attributes && (
        <div className={`divide-y divide-slate-100 mt-2 border-t border-slate-100 pt-1${archivedRows}`}>
          <RefRow
            label={t('tickets:validation.fields.status')}
            value={
              attributes.status
                ? attributes.status === 'HOMES_CONNECT'
                  ? (
                      <span className="inline-flex items-center gap-1.5 font-semibold text-red-700">
                        <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-red-600 text-white text-[10px] font-bold leading-none">!</span>
                        {attributes.status}
                      </span>
                    )
                  : attributes.status
                : '—'
            }
          />
          {/* 22-10 — l'UTAC, seulement sur un HOMES_CONNECT (un UTAC n'existe que là ; le faux ODF en pose un qui
              n'est pas une connexion). */}
          {attributes.status === 'HOMES_CONNECT' && (
            <RefRow
              label={t('tickets:validation.fields.utac')}
              value={attributes.utac ? <span className="font-mono">{attributes.utac}</span> : '—'}
            />
          )}
          {/* Blocked delivery codes (2001-2015 / 2101-2112, user annex 2026-08-03): decoded to their
              exact blocking reason + shown RED so a blocked address can't be missed. */}
          <RefRow
            label={t('tickets:validation.fields.deliveryStatus')}
            value={
              isBlockedDeliveryCode(attributes.deliveryStatus) ? (
                <span className="inline-flex items-center gap-1.5 font-semibold text-red-700">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                  {formatDeliveryStatus(attributes.deliveryStatus)}
                </span>
              ) : formatDeliveryStatus(attributes.deliveryStatus)
            }
          />
          {/* WHY the address is not Homes-Passed (reason_not_homes_passed, annex A32). Shown only when the
              DMP carries one — 2 062 production addresses do, and until now the app answered none of them.
              Amber, not red: this is an explanation, not a blocker the reviewer caused. The raw code is kept
              next to the label so an unmapped future code still says something. */}
          {attributes.reasonNotHomesPassed != null && attributes.reasonNotHomesPassed !== 0 && (
            <RefRow
              label={t('tickets:validation.fields.notHomesPassedReason')}
              value={
                <span className="inline-flex items-start gap-1.5 font-medium text-amber-700">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <span>
                    {attributes.reasonNotHomesPassed}
                    {NOT_HOMES_PASSED_REASONS[attributes.reasonNotHomesPassed]
                      ? ` — ${NOT_HOMES_PASSED_REASONS[attributes.reasonNotHomesPassed]}`
                      : ''}
                  </span>
                </span>
              }
            />
          )}
          {/* Only when the DMP row carries a value (user ask): no noisy '—' row for the common empty case. */}
          {attributes.lengthEstimateFromStreet != null && (
            <RefRow
              label={t('tickets:validation.fields.lengthEstimate')}
              value={`${attributes.lengthEstimateFromStreet} m`}
            />
          )}
          <RefRow label={t('tickets:validation.fields.deploymentList')} value={attributes.scopelistV15 ?? '—'} />
          <RefRow label={t('tickets:validation.fields.buildList')} value={attributes.scopelistV2 ?? '—'} />
          <RefRow label={t('tickets:validation.fields.twinJvid')} value={attributes.twinJvid ?? '—'} />
          {/* DMP release block — shown only when set (BLOCKED_BY_SYNDICUS, DESIGN_PROBLEM, …): the
              reviewer must see WHY this address must not be released blindly. */}
          {attributes.homeReleaseBlockedReason && (
            <RefRow
              label={t('tickets:validation.fields.releaseBlocked')}
              value={
                <span className="inline-flex items-center gap-1.5 font-semibold text-red-700">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                  {attributes.homeReleaseBlockedReason}
                  <span className="font-normal text-slate-500">
                    {attributes.homeReleaseBlockedAt ? ` ${t('tickets:validation.fields.releaseBlockedSince', { date: attributes.homeReleaseBlockedAt })}` : ''}
                    {attributes.homeReleaseBlockedUntil ? ` → ${attributes.homeReleaseBlockedUntil}` : ''}
                  </span>
                </span>
              }
            />
          )}
        </div>
      )}
    </>
  );
}

// ── « Visible chez le partenaire ? » ─────────────────────────────────────────────────────────────
// Demande user 2026-08-31, née de deux cas réels : une adresse retirée du scope par une seule date
// (out_of_scope_at), et une autre « officiellement released » qui n'arrivait pas dans le service file
// parce que home_released n'était pas coché. Dans les deux cas la réponse était dans le bloc brut des
// 105 colonnes, à retrouver et comparer à la main.
//
// Trois partis pris, tirés de ces deux cas :
//  1. LE VERDICT D'ABORD. La fiche affichait `Status: HOMES_PASSED` en évidence, ce qui se lit comme
//     « c'est publié » — alors que le statut n'est qu'une condition sur dix.
//  2. CE QUI BLOQUE EST DÉPLIÉ, CE QUI PASSE EST REPLIÉ. Neuf lignes vertes noieraient l'unique rouge.
//  3. LE FICHIER EST DATÉ. Sans confronter l'absence à la date de l'export, on crierait au loup
//     8 434 fois (mesure du 31-08) : une adresse publiée après l'export ne peut pas y figurer.
function ServiceFileVerdict({
  attributes, buildFiles, renderFix,
}: {
  attributes: DmpAddressAttributes;
  buildFiles?: BuildFileHit[];
  renderFix?: (checkId: ServiceFileCheckId) => ReactNode;
}) {
  const { t } = useTranslation(['tickets']);
  const [open, setOpen] = useState(false);
  const el = serviceFileEligibility(attributes);
  if (el.checks.length === 0) return null;

  const service = buildFiles?.find((f) => f.kind === 'SERVICE');
  // La date de l'EXPORT (lue dans son nom), à défaut celle du chargement — R1, 2026-09-25.
  const exportedAt = service ? (service.fileDate ?? service.uploadedAt) : null;
  const presence = serviceFilePresence({
    found: service ? service.found : null,
    exportedAt,
    homeReleasedDate: attributes.homeReleasedDate,
    updatedAt: attributes.updatedAt,
    eligible: el.eligible,
  });

  const ok = el.eligible;
  const passing = el.checks.filter((c) => c.ok === true);
  // Une anomalie = éligible, absente, et rien n'a bougé depuis l'export : le seul cas où l'absence
  // n'est explicable ni par une condition ni par le calendrier. C'est le signal qui mérite du rouge.
  const anomaly = presence === 'anomaly';

  return (
    <div className={`mb-3 rounded-lg border ${ok && !anomaly ? 'border-emerald-200 bg-emerald-50/50'
      : anomaly ? 'border-amber-300 bg-amber-50' : 'border-red-200 bg-red-50/60'} px-3 py-2`}>
      <div className="flex items-start gap-2">
        {ok && !anomaly
          ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
          : <XCircle className={`mt-0.5 h-4 w-4 shrink-0 ${anomaly ? 'text-amber-600' : 'text-red-600'}`} />}
        <div className="min-w-0 flex-1">
          <p className={`text-xs font-semibold ${ok && !anomaly ? 'text-emerald-800' : anomaly ? 'text-amber-800' : 'text-red-800'}`}>
            {ok
              ? t('tickets:validation.serviceFile.yes')
              : t('tickets:validation.serviceFile.no', { count: el.blocking.length })}
          </p>

          {/* Ce qui bloque, nommé — jamais replié. */}
          {el.blocking.length > 0 && (
            <ul className="mt-1 space-y-0.5">
              {el.blocking.map((c) => (
                <li key={c.id} className="flex items-start gap-1.5 text-[11px] text-red-800">
                  <XCircle className="mt-[3px] h-3 w-3 shrink-0" />
                  <span className="min-w-0 flex-1">
                    <b>{t(`tickets:validation.serviceFile.checks.${c.id}`)}</b>
                    {' — '}{t(`tickets:validation.serviceFile.fail.${c.id}`, { value: c.detail ?? '' })}
                    {/* Le bouton de réparation, quand l'écran en fournit un : la console DMP, et la fiche par logement
                        de la fenêtre de validation pour les seuls identifiants réseau d'un MDU (D2708-2). */}
                    {renderFix?.(c.id)}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {/* Non vérifiable (repli snapshot, DMP injoignable) : dit, mais pas compté comme un blocage. */}
          {el.unknown.length > 0 && (
            <p className="mt-1 flex items-start gap-1.5 text-[11px] text-slate-500">
              <HelpCircle className="mt-[3px] h-3 w-3 shrink-0" />
              {t('tickets:validation.serviceFile.unknown', {
                fields: el.unknown.map((c) => t(`tickets:validation.serviceFile.checks.${c.id}`)).join(', '),
              })}
            </p>
          )}

          {/* La confrontation au fichier réellement chargé, avec sa date. */}
          {presence !== 'noFile' && (
            <p className={`mt-1 text-[11px] ${anomaly ? 'font-semibold text-amber-800' : 'text-slate-600'}`}>
              {t(`tickets:validation.serviceFile.presence.${presence}`, {
                date: exportedAt ? new Date(exportedAt).toLocaleDateString('fr-BE') : '',
              })}
            </p>
          )}

          {passing.length > 0 && (
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 hover:text-slate-700"
            >
              {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              {t('tickets:validation.serviceFile.showPassing', { count: passing.length })}
            </button>
          )}
          {open && (
            <ul className="mt-1 space-y-0.5 border-t border-slate-200/70 pt-1">
              {passing.map((c) => (
                <li key={c.id} className="flex items-start gap-1.5 text-[11px] text-slate-600">
                  <CheckCircle2 className="mt-[3px] h-3 w-3 shrink-0 text-emerald-600" />
                  <span>
                    {t(`tickets:validation.serviceFile.checks.${c.id}`)}
                    {c.detail ? <span className="ml-1 font-mono text-slate-500">{c.detail}</span> : null}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function RefRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-2 py-1.5">
      <span className="text-xs text-slate-500">{label}</span>
      <span className="text-sm font-medium text-slate-700">{value === '' || value == null ? '—' : value}</span>
    </div>
  );
}
